import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "../components/AuthLayout";
import SocialButtons from "../components/SocialButtons";
import { useAuth } from "@/hooks/useAuth";

const passwordChecks = (pw: string) => ({
  length: pw.length >= 8,
  letter: /[A-Za-z]/.test(pw),
  number: /\d/.test(pw),
});

export default function SignupPage() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checks = useMemo(() => passwordChecks(password), [password]);
  const passwordValid = checks.length && checks.letter && checks.number;
  const strengthScore = Object.values(checks).filter(Boolean).length;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError("Please share your full name.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!passwordValid) {
      setError("Password needs 8+ characters with at least one letter and one number.");
      return;
    }
    if (!agree) {
      setError("Please agree to the terms to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await signUp({ firstName, lastName, email, password });
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Make space for the things you love."
      subtitle="Create your account in under a minute — no card needed until your cleaner finishes the visit."
      footerText="Already have a PureNest account?"
      footerLinkLabel="Sign in"
      footerLinkTo="/login"
      imageUrl="https://readdy.ai/api/search-image?query=Elegant%20sunlit%20dining%20nook%20with%20cream%20walls%20round%20wooden%20table%20linen%20chairs%20and%20fresh%20cut%20wildflowers%20in%20ceramic%20vase%2C%20warm%20afternoon%20light%20streaming%20through%20arched%20window%20casting%20soft%20long%20shadows%2C%20editorial%20interior%20photography%20premium%20warm%20neutral%20palette%20serene%20home%20atmosphere&width=1400&height=1800&seq=auth-signup-2026&orientation=portrait"
      imageAlt="Sunlit dining nook"
      quote="My cleaner Aisha shows up like clockwork and remembers exactly how I like the kitchen. It feels less like a service and more like a tiny gift each week."
      quoteAuthor="Marcus Cole"
    >
      <SocialButtons />

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-background-200"></div>
        <span className="text-xs text-foreground-600 uppercase tracking-widest">or with email</span>
        <div className="flex-1 h-px bg-background-200"></div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="auth-signup-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="signup-first" className="block text-sm font-medium text-foreground-800 mb-1.5">
              First name
            </label>
            <input
              id="signup-first"
              name="firstName"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Eleanor"
              className="w-full px-4 py-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
            />
          </div>
          <div>
            <label htmlFor="signup-last" className="block text-sm font-medium text-foreground-800 mb-1.5">
              Last name
            </label>
            <input
              id="signup-last"
              name="lastName"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Whitcombe"
              className="w-full px-4 py-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
            />
          </div>
        </div>

        <div>
          <label htmlFor="signup-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
            Email
          </label>
          <div className="relative">
            <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-600"></i>
            <input
              id="signup-email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full pl-11 pr-4 py-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
            />
          </div>
        </div>

        <div>
          <label htmlFor="signup-password" className="block text-sm font-medium text-foreground-800 mb-1.5">
            Password
          </label>
          <div className="relative">
            <i className="ri-lock-2-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-600"></i>
            <input
              id="signup-password"
              name="password"
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              className="w-full pl-11 pr-12 py-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground-600 hover:text-foreground-900 cursor-pointer"
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              <i className={showPw ? "ri-eye-off-line" : "ri-eye-line"}></i>
            </button>
          </div>

          {password && (
            <div className="mt-3 space-y-2">
              <div className="flex gap-1.5">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className={`flex-1 h-1 rounded-full transition-colors ${
                      strengthScore > i
                        ? strengthScore === 3
                          ? "bg-primary-500"
                          : strengthScore === 2
                          ? "bg-secondary-500"
                          : "bg-accent-500"
                        : "bg-background-200"
                    }`}
                  />
                ))}
              </div>
              <ul className="grid grid-cols-3 gap-1.5 text-xs">
                <Check ok={checks.length} label="8+ chars" />
                <Check ok={checks.letter} label="A letter" />
                <Check ok={checks.number} label="A number" />
              </ul>
            </div>
          )}
        </div>

        <label className="flex items-start gap-2.5 text-sm text-foreground-700 cursor-pointer select-none">
          <span
            onClick={() => setAgree((v) => !v)}
            className={`mt-0.5 w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-md border transition-colors ${
              agree
                ? "bg-primary-500 border-primary-500 text-background-50"
                : "border-background-300 bg-background-50"
            }`}
          >
            {agree && <i className="ri-check-line text-sm"></i>}
          </span>
          <span>
            I agree to the{" "}
            <a href="#" className="text-primary-600 font-medium hover:text-primary-700">
              Terms
            </a>{" "}
            and{" "}
            <a href="#" className="text-primary-600 font-medium hover:text-primary-700">
              Privacy Policy
            </a>
            .
          </span>
        </label>

        {error && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-accent-100 text-accent-900 text-sm">
            <i className="ri-error-warning-line mt-0.5"></i>
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="group w-full flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Creating your account…
            </>
          ) : (
            <>
              Create my account
              <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
            </>
          )}
        </button>

        <p className="text-xs text-foreground-600 text-center">
          No charges until your first visit is complete.
        </p>
      </form>
    </AuthLayout>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li
      className={`flex items-center gap-1.5 ${
        ok ? "text-primary-700" : "text-foreground-600"
      }`}
    >
      <span
        className={`w-4 h-4 flex items-center justify-center rounded-full ${
          ok ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
        }`}
      >
        <i className={`${ok ? "ri-check-line" : "ri-close-line"} text-xs`}></i>
      </span>
      {label}
    </li>
  );
}